import { useMessageSource } from 'src/i18n/useMessageSource'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { Dispatch, useCallback, useMemo } from 'react'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { Chip, IconButton } from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/shared/icons/Icons'
import { CriteriaGroupsGridToolbar } from 'src/screens/administration/configuration-management/criteria-configuration/criteria-group/CriteriaGroupsGridToolbar'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { S } from 'src/shared/styled/S'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
} from 'src/shared/constants/table-settings-constants'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import {
  getTranslationForProcess,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'

interface CriteriaGroupsGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
  setCriteriaGroupToBeDeleted: Dispatch<number>
}

export const CriteriaGroupsGrid = ({
  dataSettings,
  tableSettings,
  setTableSettings,
  setCriteriaGroupToBeDeleted,
}: CriteriaGroupsGridProps) => {
  const { rows, rowsCount, gridLoading } = dataSettings
  const { sortModel, paginationModel } = tableSettings
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const columns = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: getMessage('label.configuration.evaluation.criteria.grid.id'),
        flex: 0.5,
        sortable: true,
        filterable: false,
      },
      {
        field: 'groupTitle',
        headerName: getMessage('label.configuration.criteria.group.grid.title'),
        flex: 2.5,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <S.Box.TruncateBox>
            <HtmlRenderer html={row.groupTitle} truncate variant="body2" />
          </S.Box.TruncateBox>
        ),
      },
      {
        field: 'allocation',
        headerName: getMessage('label.configuration.grid.allocation'),
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          if (params.value) {
            return <Chip label={getTranslationForProcess(getMessage, params.value)} />
          }
          return ''
        },
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.5,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const onEditClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const groupId = params.row.id
            const route = ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationCriteriaGroupsEdit.params({
              groupId,
            })
            navigate(route)
          }
          const onDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()

            const groupId = params.row.id
            setCriteriaGroupToBeDeleted(groupId)
          }

          return (
            <>
              {params.row.is_deletable && (
                <span>
                  <IconButton onClick={onDeleteClick} color="primary">
                    <DeleteIcon />
                  </IconButton>
                </span>
              )}
              <span>
                <IconButton onClick={onEditClick} color="primary">
                  <EditIcon />
                </IconButton>
              </span>
            </>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, navigate, setCriteriaGroupToBeDeleted])

  const handleAddCriteriaGroupClick = useCallback(() => {
    navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationCriteriaGroupsCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        disableColumnPinning
        disableRowSelectionOnClick
        disableColumnMenu
        pagination
        paginationModel={paginationModel}
        sortModel={sortModel}
        pageSizeOptions={[10, 25, 50]}
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={(newPagination) => {
          setTableSettings({
            ...tableSettings,
            paginationModel: newPagination,
          })
        }}
        onSortModelChange={(newSort) => {
          setTableSettings({
            ...tableSettings,
            sortModel: newSort,
          })
        }}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <CriteriaGroupsGridToolbar numResults={rowsCount} onAddCriteriaGroupClick={handleAddCriteriaGroupClick} />
          ),
        }}
      />
    </DataGridContainer>
  )
}
